import { useCallback, useEffect, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

// ── Types matching the analytics Netlify Function response ──────────────────────

interface CurrencyMap { [currency: string]: number }

interface Totals {
  pageviews: number;
  payments: number;
  invoices: number;
  paymentFiatByCurrency: CurrencyMap;
  invoiceFiatByCurrency: CurrencyMap;
  paymentBchTotal: number;
  firstSeen?: number;
}

interface DayStats {
  date: string;
  pageviews: number;
  payments: number;
  invoices: number;
  paymentFiatByCurrency: CurrencyMap;
  invoiceFiatByCurrency: CurrencyMap;
  paymentBchTotal: number;
  tipFiatByCurrency?: CurrencyMap;
  paths?: Record<string, number>;
}

interface EventRecord {
  type: 'pageview' | 'payment' | 'invoice';
  ts: number;
  fiatAmount?: number;
  bchAmount?: number;
  tipAmount?: number;
  total?: number;
  currency?: string;
  offline?: boolean;
  itemCount?: number;
  path?: string;
  ref?: string;
}

interface StatsResponse {
  totals: Totals | null;
  day: DayStats | null;
  daily: DayStats[];
  events?: EventRecord[];
}

const TOKEN_KEY = 'bchpay:analytics-token';

function fmtMoney(map: CurrencyMap | undefined): string {
  if (!map || Object.keys(map).length === 0) return '—';
  return Object.entries(map)
    .map(([cur, amt]) => `${amt.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${cur}`)
    .join(' · ');
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5">
      <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">{label}</p>
      <p className="mt-2 text-3xl font-extrabold text-zinc-900 dark:text-zinc-50">{value}</p>
      {sub && <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{sub}</p>}
    </div>
  );
}

function TokenGate({ onSubmit }: { onSubmit: (t: string) => void }) {
  const [value, setValue] = useState('');
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-6 space-y-4">
        <div>
          <h1 className="text-xl font-extrabold text-zinc-900 dark:text-zinc-50">Analytics</h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Enter your stats token to view BCHPAY.Cash usage.
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) onSubmit(value.trim());
          }}
          className="space-y-3"
        >
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Stats token"
            autoComplete="off"
            className="w-full rounded-xl border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-4 py-3 text-zinc-900 dark:text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0AC18E]"
          />
          <button
            type="submit"
            className="w-full rounded-xl bg-[#0AC18E] px-4 py-3 font-bold text-white hover:bg-[#09ad7f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0AC18E] focus-visible:ring-offset-2"
          >
            View analytics
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onReset }: { token: string; onReset: () => void }) {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/.netlify/functions/analytics?token=${encodeURIComponent(token)}&events=1`
      );
      if (res.status === 401) {
        setError('unauthorized');
        return;
      }
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body?.detail || body?.error || '';
        } catch {
          /* ignore */
        }
        setError(`Server error (${res.status})${detail ? `: ${detail}` : ''}`);
        return;
      }
      setData(await res.json());
    } catch {
      setError('Could not reach the analytics server. Is the site deployed to Netlify?');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const clearData = useCallback(async () => {
    const confirmed = window.confirm(
      'Clear ALL analytics data? This permanently deletes recorded visits, payments, and invoices. This cannot be undone.'
    );
    if (!confirmed) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/.netlify/functions/analytics?token=${encodeURIComponent(token)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        setError(`Reset failed (${res.status})`);
        return;
      }
      await load();
    } catch {
      setError('Could not reach the analytics server to reset data.');
    } finally {
      setLoading(false);
    }
  }, [token, load]);

  useEffect(() => {
    load();
  }, [load]);

  if (error === 'unauthorized') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900 px-4">
        <div className="max-w-sm text-center space-y-4">
          <p className="text-zinc-700 dark:text-zinc-200">Invalid stats token.</p>
          <button onClick={onReset} className="text-[#0AC18E] font-bold underline">
            Try a different token
          </button>
        </div>
      </div>
    );
  }

  const totals = data?.totals;
  const day = data?.day;
  const daily = data?.daily ?? [];
  const events = (data?.events ?? []).slice().reverse();

  const chartData = daily.map((d) => ({
    date: d.date.slice(5), // MM-DD
    Visits: d.pageviews,
    Payments: d.payments,
    Invoices: d.invoices,
  }));

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
      <div className="mx-auto max-w-5xl px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold text-zinc-900 dark:text-zinc-50">
              BCHPAY.Cash Analytics
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Anonymous usage across all visitors — Nostr or not.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="rounded-xl border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-sm font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              Refresh
            </button>
            <button
              onClick={clearData}
              className="rounded-xl border border-red-300 dark:border-red-800 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
            >
              Clear data
            </button>
            <button
              onClick={onReset}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              Sign out
            </button>
          </div>
        </div>

        {loading && !data && (
          <p className="text-zinc-500 dark:text-zinc-400">Loading…</p>
        )}
        {error && error !== 'unauthorized' && (
          <div className="rounded-2xl border border-dashed border-red-300 dark:border-red-800 p-6 text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total visits" value={(totals?.pageviews ?? 0).toLocaleString()} />
          <StatCard
            label="Payments completed"
            value={(totals?.payments ?? 0).toLocaleString()}
            sub={fmtMoney(totals?.paymentFiatByCurrency)}
          />
          <StatCard
            label="Invoices created"
            value={(totals?.invoices ?? 0).toLocaleString()}
            sub={fmtMoney(totals?.invoiceFiatByCurrency)}
          />
          <StatCard
            label="Total BCH received"
            value={(totals?.paymentBchTotal ?? 0).toLocaleString(undefined, { maximumFractionDigits: 8 })}
          />
        </div>

        {/* Today */}
        {day && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Visits today" value={day.pageviews.toLocaleString()} />
            <StatCard label="Payments today" value={day.payments.toLocaleString()} sub={fmtMoney(day.paymentFiatByCurrency)} />
            <StatCard label="Invoices today" value={day.invoices.toLocaleString()} sub={fmtMoney(day.invoiceFiatByCurrency)} />
            <StatCard label="Tips today" value={fmtMoney(day.tipFiatByCurrency)} />
          </div>
        )}

        {/* 30-day chart */}
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">
            Last 30 days
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Visits" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Payments" fill="#0AC18E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Invoices" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent events */}
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">
            Recent activity (today)
          </h2>
          {events.length === 0 ? (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">No events recorded today yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">
                    <th className="py-2 pr-4 font-semibold">Time</th>
                    <th className="py-2 pr-4 font-semibold">Type</th>
                    <th className="py-2 pr-4 font-semibold">Amount</th>
                    <th className="py-2 pr-4 font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.slice(0, 100).map((ev, i) => (
                    <tr key={i} className="border-b border-zinc-100 dark:border-zinc-800 last:border-0">
                      <td className="py-2 pr-4 whitespace-nowrap text-zinc-500 dark:text-zinc-400">
                        {new Date(ev.ts).toLocaleTimeString()}
                      </td>
                      <td className="py-2 pr-4">
                        <span
                          className={
                            ev.type === 'payment'
                              ? 'text-[#0AC18E] font-semibold'
                              : ev.type === 'invoice'
                              ? 'text-indigo-500 font-semibold'
                              : 'text-zinc-500'
                          }
                        >
                          {ev.type}
                        </span>
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-zinc-700 dark:text-zinc-200">
                        {ev.type === 'payment' && ev.fiatAmount != null
                          ? `${ev.fiatAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ev.currency ?? ''}`
                          : ev.type === 'invoice' && ev.total != null
                          ? `${ev.total.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${ev.currency ?? ''}`
                          : '—'}
                      </td>
                      <td className="py-2 pr-4 text-zinc-500 dark:text-zinc-400">
                        {ev.type === 'pageview' && (ev.path ?? '/')}
                        {ev.type === 'payment' && ev.offline && 'offline'}
                        {ev.type === 'payment' && ev.bchAmount != null && !ev.offline
                          ? `${ev.bchAmount.toFixed(8)} BCH`
                          : ''}
                        {ev.type === 'invoice' && ev.itemCount != null && `${ev.itemCount} item(s)`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-zinc-400">
          Data stored privately in Netlify Blobs · No cookies · No personal data
        </p>
      </div>
    </div>
  );
}

export function AnalyticsRoot() {
  useSeoMeta({
    title: 'Analytics · BCHPAY.Cash',
    description: 'Private usage analytics for BCHPAY.Cash.',
  });

  const [token, setToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });

  const saveToken = (t: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch {
      /* ignore */
    }
    setToken(t);
  };

  const clearToken = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
  };

  if (!token) return <TokenGate onSubmit={saveToken} />;
  return <Dashboard token={token} onReset={clearToken} />;
}

export default AnalyticsRoot;
